import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { TournamentService } from '../../services/tournament.service';
import { AuthenticationService } from '../../services/authentication.service';
import {
  Tournament,
  TournamentStatus,
  TournamentTeamFormat,
  TournamentRegistration,
  TournamentRegistrationStatus,
  Bracket,
} from '../../model/tournament';
import { PermissionEnum } from '../../model/permission-enum';
import { PartnerPickerComponent, PartnerSelection } from '../partner-picker/partner-picker.component';
import { TournamentBracketComponent } from '../tournament-bracket/tournament-bracket.component';

@Component({
  selector: 'app-tournament-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule, PartnerPickerComponent, TournamentBracketComponent],
  templateUrl: './tournament-detail.component.html',
  styleUrl: './tournament-detail.component.css',
})
export class TournamentDetailComponent implements OnInit {
  tournament: Tournament | null = null;
  loading = true;
  notFound = false;

  bracket: Bracket | null = null;
  isAdmin = false;

  myRegistration: TournamentRegistration | null = null;
  isPartner = false;
  selectedPartner: PartnerSelection | null = null;
  submitting = false;
  actionError = '';

  TournamentStatus = TournamentStatus;
  TournamentTeamFormat = TournamentTeamFormat;
  RegStatus = TournamentRegistrationStatus;

  constructor(
    private route: ActivatedRoute,
    private tournamentService: TournamentService,
    private auth: AuthenticationService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.auth.waitForInitialization();
    const perms = this.auth.currentUserPermissions ?? [];
    this.isAdmin = perms.some((p: PermissionEnum) => p == PermissionEnum.Admin);

    const id = Number(this.route.snapshot.params['id']);
    this.tournamentService.getById(id).subscribe({
      next: t => {
        this.tournament = t;
        this.loading = false;
        this.loadMyRegistration(id);
        if (t.status >= TournamentStatus.Drawn) this.loadBracket(id);
      },
      error: () => { this.notFound = true; this.loading = false; },
    });
  }

  private loadBracket(id: number): void {
    this.tournamentService.getBracket(id).subscribe({
      next: b => { this.bracket = b; },
      error: () => { this.bracket = null; },
    });
  }

  private loadMyRegistration(id: number): void {
    this.tournamentService.getMine().subscribe({
      next: mine => {
        const row = mine.find(m => m.tournament.id === id);
        this.myRegistration = row ? row.my_registration : null;
        this.isPartner = row ? row.is_partner : false;
      },
      error: () => { this.myRegistration = null; this.isPartner = false; },
    });
  }

  get isPairs(): boolean {
    return this.tournament?.team_format === TournamentTeamFormat.Pairs;
  }

  get registrationOpen(): boolean {
    if (!this.tournament) return false;
    if (this.tournament.status !== TournamentStatus.Open) return false;
    return new Date(this.tournament.registration_deadline).getTime() > Date.now();
  }

  get canRegister(): boolean {
    return this.registrationOpen && !this.myRegistration;
  }

  get canConfirm(): boolean {
    return this.registrationOpen && this.isPartner
      && this.myRegistration?.status === TournamentRegistrationStatus.PendingPartner;
  }

  get canWithdraw(): boolean {
    return this.registrationOpen && !this.isPartner && !!this.myRegistration
      && (this.myRegistration.status === TournamentRegistrationStatus.Confirmed
        || this.myRegistration.status === TournamentRegistrationStatus.PendingPartner);
  }

  onPartnerSelected(p: PartnerSelection): void {
    this.selectedPartner = p;
  }

  register(): void {
    if (!this.tournament) return;
    if (this.isPairs && !this.selectedPartner) { this.actionError = 'Select a partner first.'; return; }
    this.submitting = true;
    this.actionError = '';
    this.tournamentService.register(this.tournament.id, this.selectedPartner?.id).subscribe({
      next: () => { this.submitting = false; this.reload(); },
      error: err => { this.submitting = false; this.actionError = this.readError(err); },
    });
  }

  confirm(): void {
    if (!this.tournament || !this.myRegistration) return;
    this.submitting = true;
    this.actionError = '';
    this.tournamentService.confirmRegistration(this.tournament.id, this.myRegistration.id).subscribe({
      next: () => { this.submitting = false; this.reload(); },
      error: err => { this.submitting = false; this.actionError = this.readError(err); },
    });
  }

  withdraw(): void {
    if (!this.tournament) return;
    this.submitting = true;
    this.actionError = '';
    this.tournamentService.cancelRegistration(this.tournament.id).subscribe({
      next: () => { this.submitting = false; this.reload(); },
      error: err => { this.submitting = false; this.actionError = this.readError(err); },
    });
  }

  private reload(): void {
    if (this.tournament) {
      this.selectedPartner = null;
      this.tournamentService.getById(this.tournament.id).subscribe(t => { this.tournament = t; });
      this.loadMyRegistration(this.tournament.id);
    }
  }

  private readError(err: any): string {
    return typeof err?.error === 'string' ? err.error : 'Something went wrong. Please try again.';
  }
}
